/**
 * Login/Signup Page
 *
 * Handles user authentication with email/password.
 * Supports both login and signup modes with comprehensive validation.
 *
 * Security features:
 * - Email format validation
 * - Password strength requirements (signup)
 * - Clear error messages (without security leaks)
 * - Email verification flow
 * - Rate limiting (backend)
 *
 * UX features:
 * - Toggle between login/signup modes
 * - Show/hide password
 * - Loading states
 * - Remember intended destination
 * - Forgot password flow
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import '../styles/LoginPage.css';

interface LocationState {
  from?: string;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { signIn, signUp, resetPassword, user } = useAuth();

  // Determine mode from URL query param (?mode=signup)
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>(
    searchParams.get('mode') === 'signup' ? 'signup' : 'login'
  );

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Validation state
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const locationState = location.state as LocationState | null;
      const from = locationState?.from || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  /**
   * Validate email format
   */
  const validateEmail = (email: string): boolean => {
    if (!email) {
      setEmailError('Email is required');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }

    setEmailError('');
    return true;
  };

  /**
   * Validate password strength (for signup)
   */
  const validatePassword = (password: string): boolean => {
    if (!password) {
      setPasswordError('Password is required');
      return false;
    }

    if (mode === 'signup') {
      if (password.length < 8) {
        setPasswordError('Password must be at least 8 characters');
        return false;
      }

      if (!/[A-Z]/.test(password)) {
        setPasswordError('Password must contain at least one uppercase letter');
        return false;
      }

      if (!/[a-z]/.test(password)) {
        setPasswordError('Password must contain at least one lowercase letter');
        return false;
      }

      if (!/[0-9]/.test(password)) {
        setPasswordError('Password must contain at least one number');
        return false;
      }

      if (!/[^A-Za-z0-9]/.test(password)) {
        setPasswordError('Password must contain at least one special character');
        return false;
      }
    }

    setPasswordError('');
    return true;
  };

  /**
   * Get password strength indicator
   */
  const getPasswordStrength = (password: string): { strength: number; label: string; color: string } => {
    if (!password) return { strength: 0, label: '', color: '' };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength, label: 'Weak', color: '#ef4444' };
    if (strength <= 4) return { strength, label: 'Fair', color: '#f59e0b' };
    if (strength <= 5) return { strength, label: 'Good', color: '#10b981' };
    return { strength, label: 'Strong', color: '#059669' };
  };

  /**
   * Handle login
   */
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateEmail(email) || !validatePassword(password)) {
      return;
    }

    setLoading(true);

    try {
      const { error } = await signIn(email, password);

      if (error) {
        // Generic error message to avoid user enumeration
        if (error.message.includes('Invalid')) {
          setError('Invalid email or password');
        } else {
          setError('An error occurred. Please try again.');
        }
        return;
      }

      // Success - useAuth will handle redirect via ProtectedRoute
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle signup
   */
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateEmail(email) || !validatePassword(password)) {
      return;
    }

    setLoading(true);

    try {
      const { error } = await signUp(email, password, fullName);

      if (error) {
        if (error.message.includes('already registered')) {
          setError('An account with this email already exists');
        } else if (error.message.includes('Password')) {
          setError('Password does not meet requirements');
        } else {
          setError('Failed to create account. Please try again.');
        }
        return;
      }

      // Success - auto-login is handled by AuthContext, so redirect to dashboard
      setSuccess('Account created! Redirecting you to your dashboard...');
      setEmail('');
      setPassword('');
      setFullName('');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Signup error:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle forgot password
   */
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateEmail(email)) {
      return;
    }

    setLoading(true);

    try {
      const { error } = await resetPassword(email);

      if (error) {
        // Generic message to avoid user enumeration
        setError('An error occurred. Please try again.');
        return;
      }

      setSuccess('Password reset email sent! Check your inbox.');
      setEmail('');

      setTimeout(() => {
        setMode('login');
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Reset password error:', err);
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = mode === 'signup' ? getPasswordStrength(password) : null;

  return (
    <div className="login-page">
      <div className="login-container">
        {/* Left side - Branding */}
        <div className="login-branding">
          <Link to="/" className="login-brand">
            <img src="/logo.png" alt="Arber Logo" className="login-logo" />
            <span className="login-brand-text">Arber</span>
          </Link>
          <h2 className="login-tagline">
            Find guaranteed profits in sports betting
          </h2>
          <p className="login-description">
            Join thousands of smart bettors who use Arber to find arbitrage
            opportunities across 90+ sportsbooks.
          </p>
          <div className="login-features">
            <div className="login-feature">
              <span className="login-feature-icon">✓</span>
              <span>Real-time odds scanning</span>
            </div>
            <div className="login-feature">
              <span className="login-feature-icon">✓</span>
              <span>90+ sportsbooks covered</span>
            </div>

            <div className="login-feature">
              <span className="login-feature-icon">✓</span>
              <span>+EV bet calculator</span>
            </div>
          </div>
        </div>

        {/* Right side - Form */}
        <div className="login-form-container">
          <div className="login-form-wrapper">
            <h1 className="login-form-title">
              {mode === 'login' && 'Welcome back'}
              {mode === 'signup' && 'Create your account'}
              {mode === 'forgot' && 'Reset password'}
            </h1>
            <p className="login-form-subtitle">
              {mode === 'login' && 'Log in to access your dashboard'}
              {mode === 'signup' && 'Start finding arbitrage opportunities'}
              {mode === 'forgot' && "Enter your email and we'll send you a reset link"}
            </p>

            {/* Success Message */}
            {success && (
              <div className="alert alert-success">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="alert-icon"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
                {success}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="alert alert-error">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="alert-icon"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {error}
              </div>
            )}

            {/* Forms */}
            {mode === 'login' && (
              <form onSubmit={handleLogin} className="login-form">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError('');
                    }}
                    onBlur={() => validateEmail(email)}
                    placeholder="you@example.com"
                    className={emailError ? 'input-error' : ''}
                    disabled={loading}
                    autoComplete="email"
                  />
                  {emailError && <span className="field-error">{emailError}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <div className="password-input-wrapper">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError('');
                      }}
                      placeholder="Enter your password"
                      className={passwordError ? 'input-error' : ''}
                      disabled={loading}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                  {passwordError && <span className="field-error">{passwordError}</span>}
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setMode('forgot')}
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? 'Logging in...' : 'Log In'}
                </button>

                <p className="form-footer">
                  Don&apos;t have an account?{' '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setMode('signup')}
                  >
                    Sign up
                  </button>
                </p>
              </form>
            )}

            {mode === 'signup' && (
              <form onSubmit={handleSignup} className="login-form">
                <div className="form-group">
                  <label htmlFor="fullName">Full Name (Optional)</label>
                  <input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="John Doe"
                    disabled={loading}
                    autoComplete="name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError('');
                    }}
                    onBlur={() => validateEmail(email)}
                    placeholder="you@example.com"
                    className={emailError ? 'input-error' : ''}
                    disabled={loading}
                    autoComplete="email"
                  />
                  {emailError && <span className="field-error">{emailError}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <div className="password-input-wrapper">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError('');
                      }}
                      onBlur={() => validatePassword(password)}
                      placeholder="Create a strong password"
                      className={passwordError ? 'input-error' : ''}
                      disabled={loading}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                  {passwordError && <span className="field-error">{passwordError}</span>}

                  {/* Password Strength Indicator */}
                  {password && passwordStrength && (
                    <div className="password-strength">
                      <div className="password-strength-bar">
                        <div
                          className="password-strength-fill"
                          style={{
                            width: `${(passwordStrength.strength / 6) * 100}%`,
                            backgroundColor: passwordStrength.color,
                          }}
                        />
                      </div>
                      <span
                        className="password-strength-label"
                        style={{ color: passwordStrength.color }}
                      >
                        {passwordStrength.label}
                      </span>
                    </div>
                  )}

                  <p className="password-requirements">
                    Password must be at least 8 characters and include uppercase,
                    lowercase, number, and special character.
                  </p>
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? 'Creating account...' : 'Sign Up'}
                </button>

                <p className="form-footer">
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setMode('login')}
                  >
                    Log in
                  </button>
                </p>
              </form>
            )}

            {mode === 'forgot' && (
              <form onSubmit={handleForgotPassword} className="login-form">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError('');
                    }}
                    onBlur={() => validateEmail(email)}
                    placeholder="you@example.com"
                    className={emailError ? 'input-error' : ''}
                    disabled={loading}
                    autoComplete="email"
                  />
                  {emailError && <span className="field-error">{emailError}</span>}
                </div>

                <button
                  type="submit"
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>

                <p className="form-footer">
                  Remember your password?{' '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => setMode('login')}
                  >
                    Log in
                  </button>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
